/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/systemMessages.scss';

import {AwesomeIcon, Link} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {MouseEventHandler} from 'react';
import SystemMessage from "../../models/systemMessageModel";
import {SystemMessageType} from "../../util/interfaces";
import {IconName} from '@fortawesome/fontawesome-svg-core';

interface Props {
    onDismissMessage: (messageId: string) => MouseEventHandler;
    onRenewApi: MouseEventHandler;
    messages: SystemMessage[];
    apiExpiresInDays: number | null;
}

export default function SystemMessages(props: Props) {
    const {messages, onDismissMessage, apiExpiresInDays, onRenewApi} = props;
    if (!messages.length && apiExpiresInDays === null) return null;

    function getRenderMessages(): SystemMessage[] {
        const errorMessage = getFirstMessage(SystemMessageType.ERROR);
        const warningMessage = getFirstMessage(SystemMessageType.WARNING);
        const infoMessage = getFirstMessage(SystemMessageType.INFO);
        const confirmMessage = getFirstMessage(SystemMessageType.CONFIRMATION);
        const _messages: SystemMessage[] = [];
        if (errorMessage) _messages.push(errorMessage);
        if (warningMessage) _messages.push(warningMessage);
        if (infoMessage) _messages.push(infoMessage);
        if (confirmMessage) _messages.push(confirmMessage);
        return _messages;
    }

    function getFirstMessage(messageType: SystemMessageType): SystemMessage | null {
        const remaining = messages.filter((message: SystemMessage) => message.messageType === messageType)
        if (!remaining.length) return null;
        return remaining.sort((messageA: SystemMessage, messageB: SystemMessage) => messageB.priority - messageA.priority)[0];
    }

    function renderMessages() {
        const _messages = getRenderMessages();
        if (!_messages.length) return null;
        return _messages.map((message: SystemMessage) => (
            <div key={message.id} className={classNames('system-message', message.messageType.toLowerCase())}>
                <p className='message'>{renderIcon(message.messageType)}{message.message}</p>
                <AwesomeIcon name='times' className='dismiss' onClick={onDismissMessage(message.id)}
                             title='Sluiten'/>
            </div>
        ));
    }

    function renderIcon(messageType: SystemMessageType) {
        let iconName: IconName | null = null;
        if (messageType === SystemMessageType.INFO) iconName = 'info-circle';
        else if (messageType === SystemMessageType.ERROR) iconName = 'exclamation-circle';
        else if (messageType === SystemMessageType.CONFIRMATION) iconName = 'check-circle';
        else if (messageType === SystemMessageType.WARNING) iconName = 'exclamation-triangle';
        if (!iconName) return null;
        return <AwesomeIcon name={iconName} className='message-icon'/>
    }

    function renderApiExpiry() {
        if (apiExpiresInDays === null) return null;
        const messageType = apiExpiresInDays < 7 ? SystemMessageType.ERROR : SystemMessageType.WARNING;
        const expiryText = apiExpiresInDays < 0 ? 'is verlopen' : `verloopt over ${apiExpiresInDays} dagen`;
        return (
            <div className={`system-message api-expiry ${messageType.toLowerCase()}`}>
                <p className='message'>
                    {renderIcon(messageType)}
                    De koppeling met jouw Bol account {expiryText}. Om te voorkomen dat bestellingen niet meer verwerkt
                    worden, verzoeken we je om de koppeling te vernieuwen.
                </p>
                <Link onClick={onRenewApi}>Nu vernieuwen</Link>
            </div>
        )
    }

    return (
        <div className='system-messages'>
            {renderMessages()}
            {renderApiExpiry()}
        </div>
    )
}